import React, { useState, useEffect, useCallback, useRef } from "react";
import { whatsapp as waApi, appSettings } from "../services/api";
import toast from "react-hot-toast";
import { FiRefreshCw, FiWifi, FiWifiOff, FiLink, FiLink2 } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";

// ═══════════════════════════════════════════════════════════════
// WhatsApp Settings Page — ربط وإدارة أرقام واتساب
// ═══════════════════════════════════════════════════════════════

const SESSIONS = [
  { id: "corporate", label: "رقم عملاء الشركة", desc: "الرقم المخصص للتواصل مع العملاء" },
  { id: "marketers", label: "رقم المسوقين",      desc: "الرقم المخصص للتواصل مع المسوقين" },
  { id: "abubader2", label: "رقم ابوبدر 2",      desc: "الرقم المخصص لتواصل ابوبدر 2" },
  { id: "abubader_biz", label: "رقم ابوبدر بزنس", desc: "الرقم المخصص لتواصل ابوبدر بزنس" },
];

// v3.4.287 — تسلسل إجراءات الإصلاح لكل جلسة (يُحفظ 20 دقيقة في المتصفح حتى لا يضيع عند إغلاق النافذة):
// 1) تحديث الحالة / ربط الرقم → 2) إعادة تشغيل → 3) تنظيف عميق — كل زر لا يُفعَّل إلا بعد محاولة الخطوة التي قبله
const waStepsKey = (id) => "rael:waSteps:" + id;
const loadWaSteps = (id) => { try { const v = JSON.parse(sessionStorage.getItem(waStepsKey(id)) || "null"); if (v && Date.now() - (v.ts || 0) < 20 * 60 * 1000) return { status: !!v.status, restart: !!v.restart, clean: !!v.clean }; } catch {} return { status: false, restart: false, clean: false }; };
const WA_STEP_GUIDE = [
  { k: "status", n: 1, icon: "🔄", title: "تحديث الحالة", when: "أول خطوة دائمًا، وبعد كل إجراء لمعرفة نتيجته.", does: "استعلام فقط عن حالة الرقم من السيرفر — لا يغيّر شيئًا ولا يقطع الاتصال. الضغط على «ربط الرقم» يُحتسب أيضًا كخطوة أولى." },
  { k: "restart", n: 2, icon: "↻", title: "إعادة تشغيل", when: "عندما يكون الرقم متصلًا لكن الإرسال يفشل (timeout / detached frame)، أو عندما لا يظهر QR بعد تحديث الحالة.", does: "يعيد تشغيل جلسة هذا الرقم فقط على السيرفر مع الإبقاء على ملفات الربط — غالبًا يعود متصلًا خلال 10–20 ثانية بدون مسح QR جديد." },
  { k: "clean", n: 3, icon: "🧹", title: "تنظيف عميق", when: "فقط إذا لم تنفع إعادة التشغيل: «No QR available» أو الجلسة عالقة على التهيئة لأكثر من دقيقتين.", does: "أقوى إجراء — يقتل متصفح الجلسة العالق، يمسح ملفاتها وأقفالها ويعيد بناءها من الصفر. يتطلب مسح QR (أو رمز الاقتران) من جديد بعد ~دقيقة." },
];

// v3.4.288 — لوحة «سجل عملية الربط»: أحداث سيرفر واتساب لحظيًا + إجراءات النظام والمتصفح — لمعرفة أين توقف الربط
const LOG_LEVEL = { ok: { c: "#166534", bg: "#f0fdf4", b: "#bbf7d0" }, info: { c: "#334155", bg: "#fff", b: "#e2e8f0" }, warn: { c: "#b45309", bg: "#fffbeb", b: "#fde68a" }, error: { c: "#b91c1c", bg: "#fef2f2", b: "#fecaca" } };
const LOG_SRC = { server: ["🖥", "سيرفر واتساب", "#0f766e"], app: ["⚙", "النظام", "#1d4ed8"], client: ["🧑", "المتصفح", "#7c3aed"] };
const SERVER_STATUS_AR = { connected: "متصل", qr_pending: "بانتظار مسح QR", initializing: "قيد التهيئة (يفتح المتصفح)", reconnecting: "يعيد الاتصال", disconnected: "غير متصل", absent: "الجلسة غير موجودة على السيرفر" };
const fmtLogTs = (ts) => { const d = new Date(ts); if (isNaN(d)) return ""; const t = d.toLocaleTimeString("en-GB", { hour12: false }); const today = new Date().toDateString() === d.toDateString(); return today ? t : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" }) + " " + t; };
export function LinkLogPanel({ sessionId, open, onToggle, refreshKey }) {
  const [data, setData] = useState(null); const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const load = useCallback(async () => { setBusy(true); try { const r = await waApi.linkLog(sessionId, 150); setData(r.data); setErr(""); } catch (e) { setErr(e.response?.data?.error || e.message); } setBusy(false); }, [sessionId]);
  useEffect(() => { if (!open) return; load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [open, load, refreshKey]);
  const sv = data?.server; const st = sv?.status;
  const stColor = st === "connected" ? "#166534" : st === "qr_pending" ? "#b45309" : st === "initializing" || st === "reconnecting" ? "#1d4ed8" : "#b91c1c";
  const copy = () => { try { const txt = (data?.items || []).slice().reverse().map(i => `${new Date(i.ts).toISOString()} [${LOG_SRC[i.source]?.[1] || i.source}] [${i.level}] ${i.step ? i.step + ": " : ""}${i.message}`).join("\n"); navigator.clipboard.writeText(`session=${sessionId} server=${st || "?"} phone=${sv?.phone || "-"}\n` + txt); toast.success("نُسخ السجل"); } catch { toast.error("تعذر النسخ"); } };
  const clear = async () => { if (!window.confirm("مسح سجل النظام لهذه الجلسة؟ (أحداث سيرفر واتساب تبقى)")) return; try { await waApi.linkLogClear(sessionId); load(); } catch (e) { toast.error(e.response?.data?.error || e.message); } };
  return (
    <div style={{ marginTop: 14, border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
      <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: "#0f172a", color: "#e2e8f0", cursor: "pointer", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, fontWeight: 800 }}>📋 سجل عملية الربط (لحظي)</span>
        {sv && <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 9px", borderRadius: 10, background: "#fff", color: sv.reachable ? stColor : "#b91c1c" }}>
          {sv.reachable ? `● ${SERVER_STATUS_AR[st] || st || "؟"}${sv.phone ? " +" + sv.phone : ""}${sv.hasQr ? " · QR جاهز" : ""}` : "✗ السيرفر لا يستجيب"}
        </span>}
        {sv?.reachable && sv.uptimeSec != null && <span style={{ fontSize: 10.5, opacity: 0.75 }}>تشغيل السيرفر منذ {sv.uptimeSec >= 3600 ? Math.floor(sv.uptimeSec / 3600) + " س " : ""}{Math.floor((sv.uptimeSec % 3600) / 60)} د</span>}
        <span style={{ marginInlineStart: "auto", fontSize: 11, opacity: 0.8 }}>{busy ? "⟳" : ""} {open ? "▲ إخفاء" : "▼ عرض"}</span>
      </div>
      {open && (
        <div style={{ background: "#f8fafc" }}>
          <div style={{ display: "flex", gap: 6, padding: "6px 10px", borderBottom: "1px solid #e2e8f0", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 10.5, color: "#64748b" }}>يتحدث تلقائيًا كل 5 ثوانٍ — الأحدث في الأعلى. المصادر: 🖥 سيرفر واتساب (المراحل الفعلية) · ⚙ النظام (ما أُرسل للسيرفر ونتيجته) · 🧑 المتصفح (ما ضغطته)</span>
            <span style={{ marginInlineStart: "auto", display: "flex", gap: 4 }}>
              <button onClick={load} style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>⟳ تحديث</button>
              <button onClick={copy} style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", cursor: "pointer", fontFamily: "inherit" }}>⧉ نسخ</button>
              <button onClick={clear} title="يمسح سجل النظام فقط" style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #fecaca", borderRadius: 6, background: "#fff", color: "#b91c1c", cursor: "pointer", fontFamily: "inherit" }}>🗑 مسح</button>
            </span>
          </div>
          {err && <div style={{ padding: 10, color: "#b91c1c", fontSize: 12 }}>✗ {err}</div>}
          {!data && !err && <div style={{ padding: 14, color: "#94a3b8", fontSize: 12, textAlign: "center" }}>جاري تحميل السجل…</div>}
          {data && data.items.length === 0 && <div style={{ padding: 14, color: "#94a3b8", fontSize: 12, textAlign: "center" }}>لا توجد أحداث بعد — اضغط «تحديث الحالة» أو «ربط الرقم» وستظهر المراحل هنا</div>}
          {data && data.items.length > 0 && (
            <div style={{ maxHeight: 280, overflowY: "auto", padding: 8, display: "grid", gap: 4 }}>
              {data.items.map((it, i) => { const L = LOG_LEVEL[it.level] || LOG_LEVEL.info; const S = LOG_SRC[it.source] || ["•", it.source, "#64748b"]; return (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: L.bg, border: "1px solid " + L.b, borderRadius: 7, padding: "5px 8px", fontSize: 11.5 }}>
                  <span style={{ fontFamily: "ui-monospace, Menlo, Consolas, monospace", color: "#64748b", fontSize: 10.5, whiteSpace: "nowrap", direction: "ltr", paddingTop: 1 }}>{fmtLogTs(it.ts)}</span>
                  <span title={S[1]} style={{ fontSize: 10, fontWeight: 800, color: S[2], whiteSpace: "nowrap" }}>{S[0]} {S[1]}</span>
                  <span style={{ color: L.c, flex: 1, wordBreak: "break-word", fontWeight: it.level === "error" ? 700 : 500 }}>{it.level === "error" ? "✗ " : it.level === "ok" ? "✓ " : it.level === "warn" ? "⚠ " : ""}{it.message}{it.user ? <span style={{ color: "#94a3b8", fontSize: 10 }}> — {it.user}</span> : null}</span>
                </div>); })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function SessionCard({ session, visible, onToggleVisible, hideVisibility = false }) { // v3.4.150 — يُستخدم أيضًا من نافذة ربط الواتساب في النشر للمسوقين
  const [status,   setStatus]   = useState("loading");
  const [phone,    setPhone]    = useState(null);
  const [qrData,   setQrData]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const pollRef = useRef(null);
  // v3.4.151 — الربط برقم الجوال ورمز اقتران
  const [pairOpen, setPairOpen] = useState(false);
  const [pairPhone, setPairPhone] = useState("");
  const [pairCode, setPairCode] = useState(null);
  const [pairErr, setPairErr] = useState(null);
  const [pairBusy, setPairBusy] = useState(false);
  const [pairWaiting, setPairWaiting] = useState(false);
  const pairPollRef = useRef(null);
  // v3.4.287 — تسلسل الإجراءات
  const [steps, setSteps] = useState(() => loadWaSteps(session.id));
  const markStep = useCallback((k) => setSteps(p => { const n = { ...p, [k]: true }; try { sessionStorage.setItem(waStepsKey(session.id), JSON.stringify({ ...n, ts: Date.now() })); } catch {} return n; }), [session.id]);
  const [restarting, setRestarting] = useState(false);
  // v3.4.288 — سجل عملية الربط: مفتوح افتراضيًا + مسجّل من جهة المتصفح
  const [logOpen, setLogOpen] = useState(true);
  const [logTick, setLogTick] = useState(0);
  const lastStatusRef = useRef("loading");
  const clog = useCallback((level, step, message) => { waApi.linkLogPost(session.id, { level, step, message }).catch(() => {}).finally(() => setTimeout(() => setLogTick(t => t + 1), 400)); }, [session.id]);
  const requestPairCode = async () => {
    const ph = String(pairPhone || "").trim();
    if (ph.replace(/\D/g, "").length < 9) return toast.error("أدخل رقم جوال واتساب صحيحًا");
    setPairBusy(true); setPairErr(null); setPairCode(null);
    try {
      const r = await waApi.requestPairingCode(session.id, ph);
      const d = r.data || {};
      if (d.status === "connected") { setStatus("connected"); toast.success("الرقم متصل بالفعل"); }
      else if (d.pairingCode) { setPairCode(d.pairingCode); setPairWaiting(false); clog("ok", "pair", "ظهر رمز الاقتران في المتصفح — بانتظار إدخاله في الجوال"); }
      else { setPairWaiting(true); setPairErr(d.error || null); clog("info", "pair", "بانتظار صدور رمز الاقتران" + (d.error ? " — " + d.error : "")); }
    } catch (e) { setPairErr(e.response?.data?.error || e.message); clog("error", "pair", "فشل طلب الرمز: " + (e.response?.data?.error || e.message)); }
    setPairBusy(false);
  };
  useEffect(() => {
    if (!pairWaiting || pairCode) { if (pairPollRef.current) { clearInterval(pairPollRef.current); pairPollRef.current = null; } return; }
    pairPollRef.current = setInterval(async () => {
      try {
        const r = await waApi.pairingStatus(session.id); const d = r.data || {};
        if (d.status === "connected") { setStatus("connected"); setPairWaiting(false); setPairCode(null); toast.success("تم الربط ✓"); }
        else if (d.pairingCode) { setPairCode(d.pairingCode); setPairWaiting(false); }
        else if (d.error) setPairErr(d.error);
      } catch {}
    }, 4000);
    return () => { if (pairPollRef.current) clearInterval(pairPollRef.current); };
  }, [pairWaiting, pairCode, session.id]);

  const checkStatus = useCallback(async () => {
    try {
      const res = await waApi.getStatus(session.id);
      const d = res.data;
      setStatus(d.connected ? "connected" : "disconnected");
      setPhone(d.phone || null);
      if (d.connected) { setQrData(null); setPairCode(null); setPairWaiting(false); }
      // v3.4.288 — تسجيل الانتقالات فقط (لا نسجل كل استعلام دوري)
      const now = d.connected ? "connected" : "disconnected";
      if (lastStatusRef.current !== "loading" && lastStatusRef.current !== now) clog(d.connected ? "ok" : "error", "status", d.connected ? "الرقم أصبح متصلًا" + (d.phone ? " +" + d.phone : "") : "انقطع الاتصال (حالة السيرفر: " + (d.status || "?") + ")" + (d.error ? " — " + d.error : ""));
      lastStatusRef.current = now;
    } catch {
      setStatus("disconnected");
    }
  }, [session.id, clog]);

  // v3.4.048 — الجلسة بعد التنظيف/إعادة التشغيل تحتاج 30–90 ثانية قبل توفر QR:
  // بدل الفشل الفوري «No QR available» نعيد المحاولة تلقائيًا كل 5 ثوانٍ حتى دقيقتين
  const fetchQR = useCallback(async () => {
    markStep("status"); // v3.4.287 — محاولة الربط تُحتسب خطوة أولى
    clog("info", "qr", "ضغط «ربط الرقم» — سيُطلب QR حتى 24 مرة (كل 5 ثوانٍ)"); // v3.4.288
    setLoading(true);
    const MAX_TRIES = 24;
    let gotIt = false;
    for (let attempt = 1; attempt <= MAX_TRIES && !gotIt; attempt++) {
      try {
        const res = await waApi.getQR(session.id);
        const d = res.data;
        if (d.qrDataUrl || d.qr) {
          setQrData(d.qrDataUrl || d.qr);
          gotIt = true;
          clog("ok", "qr", `ظهر QR في المتصفح (المحاولة ${attempt}) — امسحه خلال 60 ثانية`); // v3.4.288
        } else if (d.connected) {
          setStatus("connected");
          setQrData(null);
          toast.success("الرقم متصل بالفعل");
          gotIt = true;
        }
      } catch (e) {
        const msg = e.response?.data?.error || e.message || "";
        // خطأ غير «QR غير جاهز بعد» (مثل انقطاع السيرفر) — لا معنى لتكراره طويلًا
        if (!/no qr|not found|initializing|404/i.test(msg) && attempt >= 3) {
          toast.error("فشل جلب QR: " + msg);
          clog("error", "qr", `توقف طلب QR بعد ${attempt} محاولات: ${msg}`); // v3.4.288
          break;
        }
      }
      if (!gotIt && attempt < MAX_TRIES) {
        if (attempt === 1) toast("⏳ الجلسة قيد التهيئة — سيظهر QR تلقائيًا خلال لحظات…", { duration: 6000 });
        await new Promise(r => setTimeout(r, 5000));
      } else if (!gotIt) {
        toast.error("لم يتوفر QR بعد دقيقتين — جرب «إعادة تشغيل» ثم «تنظيف عميق»");
        clog("error", "qr", "لم يتوفر QR بعد دقيقتين (24 محاولة) — الجلسة عالقة على السيرفر: جرّب إعادة التشغيل ثم التنظيف العميق"); // v3.4.288
      }
    }
    setLoading(false);
  }, [session.id, markStep, clog]);

  // v3.4.046 — تنظيف عميق: علاج «فشل جلب QR: No QR available» (الجلسة عالقة على initializing
  // بسبب كروم زومبي يمسك قفل ملفات الجلسة على السيرفر) — ينفذ القتل والتنظيف وإعادة البناء تلقائيًا
  const deepClean = async () => {
    if (!window.confirm(`تنظيف عميق لجلسة ${session.label}؟\nيُستخدم عندما يفشل جلب QR («No QR available») — سيقتل متصفح الجلسة العالق وينظف ملفاتها ويعيد بناءها.\nانتظر ~دقيقة بعده ثم اضغط «ربط الرقم».`)) return;
    setLoading(true);
    markStep("clean"); // v3.4.287
    try {
      await waApi.deepClean(session.id);
      setQrData(null);
      toast.success("تم التنظيف العميق ✓ — انتظر ~دقيقة ثم اضغط «ربط الرقم»", { duration: 9000 });
      clog("ok", "clean", "اكتمل التنظيف العميق من جهة النظام — انتظر ~دقيقة ثم اضغط «ربط الرقم»"); // v3.4.288
    } catch (e) {
      toast.error("فشل التنظيف: " + (e.response?.data?.error || e.message));
      clog("error", "clean", "فشل التنظيف العميق: " + (e.response?.data?.error || e.message)); // v3.4.288
    }
    setLoading(false);
  };

  // v3.4.287 — تحديث الحالة يدويًا (الخطوة 1) مع إظهار النتيجة
  const manualCheck = async () => {
    markStep("status");
    try {
      const res = await waApi.getStatus(session.id); const d = res.data || {};
      setStatus(d.connected ? "connected" : "disconnected"); setPhone(d.phone || null);
      if (d.connected) { setQrData(null); setPairCode(null); setPairWaiting(false); toast.success(`متصل ✓${d.phone ? " +" + d.phone : ""}`); }
      else toast(`غير متصل — ${d.status || d.state || "الجلسة غير جاهزة"}`, { icon: "🔴" });
      clog(d.connected ? "ok" : "warn", "status", "تحديث الحالة يدويًا: " + (d.connected ? "متصل" + (d.phone ? " +" + d.phone : "") : "غير متصل — حالة السيرفر: " + (SERVER_STATUS_AR[d.status] || d.status || "?") + (d.error ? " — " + d.error : ""))); // v3.4.288
      lastStatusRef.current = d.connected ? "connected" : "disconnected";
    } catch (e) { setStatus("disconnected"); toast.error("تعذر الاستعلام: " + (e.response?.data?.error || e.message)); clog("error", "status", "تعذر الاستعلام عن الحالة: " + (e.response?.data?.error || e.message)); }
  };
  // v3.4.287 — إعادة تشغيل جلسة هذا الرقم فقط (الخطوة 2) — متاحة بعد محاولة تحديث الحالة/الربط
  const restartSession = async () => {
    if (!steps.status) return toast("اضغط «تحديث الحالة» أولًا", { icon: "🔒" });
    if (!window.confirm(`إعادة تشغيل جلسة ${session.label}؟\nتبقى ملفات الربط محفوظة — غالبًا يعود الرقم متصلًا خلال 10–20 ثانية بدون QR جديد.`)) return;
    setRestarting(true); markStep("restart");
    try {
      await waApi.restart(session.id);
      setQrData(null);
      toast.success("أُرسل أمر إعادة التشغيل — سيُفحص الاتصال تلقائيًا خلال 15 ثانية", { duration: 8000 });
      clog("info", "restart", "أُرسل أمر إعادة التشغيل — فحص تلقائي للاتصال بعد 15 ثانية"); // v3.4.288
      setTimeout(() => { checkStatus(); setRestarting(false); }, 15000);
    } catch (e) { toast.error("فشلت إعادة التشغيل: " + (e.response?.data?.error || e.message)); setRestarting(false); clog("error", "restart", "فشلت إعادة التشغيل: " + (e.response?.data?.error || e.message)); }
  };

  const disconnect = async () => {
    if (!window.confirm(`هل تريد فصل ${session.label}؟`)) return;
    setLoading(true);
    try {
      await waApi.disconnect(session.id);
      setStatus("disconnected");
      setPhone(null);
      setQrData(null);
      toast.success("تم فصل الرقم");
      clog("warn", "disconnect", "تم فصل الرقم من المتصفح"); // v3.4.288
    } catch (e) {
      toast.error("فشل الفصل: " + (e.response?.data?.error || e.message));
    }
    setLoading(false);
  };

  useEffect(() => {
    checkStatus();
    pollRef.current = setInterval(checkStatus, 10000);
    return () => clearInterval(pollRef.current);
  }, [checkStatus]);

  const isConnected    = status === "connected";
  const isDisconnected = status === "disconnected";

  return (
    <div style={{
      background: "#fff", borderRadius: 16,
      border: `2px solid ${isConnected ? "#86efac" : isDisconnected ? "#fca5a5" : "#e2e8f0"}`,
      overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,0.06)"
    }}>
      {/* Header */}
      <div style={{
        background: isConnected
          ? "linear-gradient(135deg,#16a34a,#22c55e)"
          : isDisconnected
            ? "linear-gradient(135deg,#dc2626,#ef4444)"
            : "linear-gradient(135deg,#64748b,#94a3b8)",
        padding: "18px 22px",
        display: "flex", alignItems: "center", justifyContent: "space-between"
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, color:"#fff" }}>
          <FaWhatsapp size={28}/>
          <div>
            <div style={{ fontWeight:700, fontSize:17 }}>{session.label}</div>
            <div style={{ fontSize:13, opacity:0.85 }}>{session.desc}</div>
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{
            background:"rgba(255,255,255,0.2)", color:"#fff",
            padding:"4px 12px", borderRadius:20, fontSize:13, fontWeight:600,
            display:"flex", alignItems:"center", gap:6
          }}>
            {isConnected ? <><FiWifi size={13}/> متصل</> :
             isDisconnected ? <><FiWifiOff size={13}/> غير متصل</> :
             "جاري الفحص..."}
          </span>
          <button onClick={manualCheck} title="تحديث الحالة" style={{
            background:"rgba(255,255,255,0.2)", border:"none", color:"#fff",
            borderRadius:8, padding:8, cursor:"pointer"
          }}>
            <FiRefreshCw size={16}/>
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding:24 }}>
        {/* Visibility Toggle */}
        {!hideVisibility && (
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          background: visible ? "#f0fdf4" : "#fef2f2",
          border: `1px solid ${visible ? "#bbf7d0" : "#fecaca"}`,
          borderRadius:10, padding:"12px 16px", marginBottom:20
        }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:14, fontWeight:600, color: visible ? "#15803d" : "#dc2626" }}>
              {visible ? "🟢 ظاهر في القائمة" : "🔴 مخفي من القائمة"}
            </span>
          </div>
          <label style={{
            position:"relative", display:"inline-block", width:48, height:26, cursor:"pointer"
          }}>
            <input type="checkbox" checked={visible} onChange={() => onToggleVisible(session.id, !visible)}
              style={{ opacity:0, width:0, height:0 }} />
            <span style={{
              position:"absolute", top:0, left:0, right:0, bottom:0,
              background: visible ? "#22c55e" : "#d1d5db",
              borderRadius:26, transition:"0.3s"
            }} />
            <span style={{
              position:"absolute", top:3, left: visible ? 24 : 3,
              width:20, height:20, background:"#fff", borderRadius:"50%",
              transition:"0.3s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)"
            }} />
          </label>
        </div>
        )}
        {/* Phone number */}
        {phone && (
          <div style={{
            background:"#f0fdf4", border:"1px solid #bbf7d0",
            borderRadius:10, padding:"12px 16px", marginBottom:20,
            display:"flex", alignItems:"center", gap:10
          }}>
            <FaWhatsapp size={20} color="#16a34a"/>
            <div>
              <div style={{ fontSize:12, color:"#16a34a", fontWeight:600 }}>الرقم المتصل</div>
              <div style={{ fontSize:16, fontWeight:700, color:"#15803d", direction:"ltr" }}>+{phone}</div>
            </div>
          </div>
        )}

        {/* QR Code */}
        {qrData && (
          <div style={{ textAlign:"center", marginBottom:20 }}>
            <div style={{ fontSize:14, color:"#374151", marginBottom:12, fontWeight:600 }}>
              📱 امسح الـ QR بتطبيق واتساب:
            </div>
            <div style={{
              display:"inline-block", padding:12,
              background:"#fff", borderRadius:12,
              border:"2px solid #e2e8f0",
              boxShadow:"0 4px 16px rgba(0,0,0,0.1)"
            }}>
              <img
                src={qrData}
                alt="QR Code"
                style={{ width:220, height:220, display:"block" }}
              />
            </div>
            <div style={{ fontSize:12, color:"#94a3b8", marginTop:10 }}>
              واتساب ← الأجهزة المرتبطة ← ربط جهاز جديد
            </div>
            <div style={{ fontSize:12, color:"#f59e0b", marginTop:6, fontWeight:600 }}>
              ⏱️ QR صالح لمدة 60 ثانية — إذا انتهت اضغط "جلب QR جديد"
            </div>
          </div>
        )}

        {/* v3.4.151 — الربط برقم الجوال ورمز اقتران (بدون كاميرا) */}
        {isDisconnected && (
          <div style={{ border: "1px solid #bfdbfe", background: "#eff6ff", borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e40af" }}>📱 أو اربط برقم الجوال ورمز (بدون مسح QR)</div>
              <button onClick={() => setPairOpen(o => !o)} style={{ background: "none", border: "none", color: "#1d4ed8", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>{pairOpen ? "إخفاء" : "عرض"}</button>
            </div>
            {pairOpen && (
              <div style={{ marginTop: 10 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <input value={pairPhone} onChange={e => setPairPhone(e.target.value)} placeholder="رقم واتساب الجوال مثل 9665XXXXXXXX"
                    style={{ flex: 1, minWidth: 200, padding: "9px 12px", border: "1px solid #bfdbfe", borderRadius: 8, fontSize: 14, fontFamily: "inherit", direction: "ltr" }} />
                  <button onClick={requestPairCode} disabled={pairBusy}
                    style={{ padding: "9px 16px", background: pairBusy ? "#94a3b8" : "#1d4ed8", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: 13, cursor: pairBusy ? "not-allowed" : "pointer", fontFamily: "inherit" }}>
                    {pairBusy ? "جاري الطلب…" : pairCode ? "طلب رمز جديد" : "طلب الرمز"}
                  </button>
                </div>
                {pairCode && (
                  <div style={{ textAlign: "center", marginTop: 12 }}>
                    <div style={{ fontSize: 12, color: "#475569", marginBottom: 6 }}>أدخل هذا الرمز في واتساب على الجوال:</div>
                    <div style={{ display: "inline-block", fontSize: 30, fontWeight: 900, letterSpacing: 6, color: "#1d4ed8", fontFamily: "ui-monospace, Menlo, Consolas, monospace", background: "#fff", border: "2px solid #bfdbfe", borderRadius: 10, padding: "8px 18px", direction: "ltr" }}>
                      {String(pairCode).replace(/[^A-Z0-9]/gi, "").replace(/^(.{4})(.{4})$/, "$1-$2")}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 8, lineHeight: 1.8 }}>
                      واتساب ← الإعدادات ← الأجهزة المرتبطة ← ربط جهاز ← <b>الربط برقم الهاتف بدلًا من ذلك</b> ← أدخل الرمز
                    </div>
                    <div style={{ fontSize: 11, color: "#f59e0b", marginTop: 4, fontWeight: 600 }}>⏱️ الرمز صالح لدقائق قليلة — إن انتهى اضغط «طلب رمز جديد»</div>
                  </div>
                )}
                {pairErr && <div style={{ marginTop: 8, fontSize: 12, color: "#b91c1c" }}>✗ {pairErr}</div>}
                {pairWaiting && !pairCode && <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>⏳ الجلسة قيد التهيئة — سيظهر الرمز تلقائيًا خلال لحظات…</div>}
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          {isDisconnected && (
            <button
              onClick={fetchQR}
              disabled={loading}
              style={{
                flex:1, minWidth:140, padding:"12px 20px",
                background: loading ? "#94a3b8" : "#25D366",
                border:"none", borderRadius:10, cursor: loading ? "not-allowed" : "pointer",
                color:"#fff", fontFamily:"inherit", fontSize:14, fontWeight:700,
                display:"flex", alignItems:"center", justifyContent:"center", gap:8
              }}
            >
              <FiLink size={16}/>
              {loading ? "جاري الجلب..." : qrData ? "جلب QR جديد" : "ربط الرقم"}
            </button>
          )}

          {qrData && (
            <button
              onClick={fetchQR}
              disabled={loading}
              style={{
                flex:1, minWidth:140, padding:"12px 20px",
                background:"#f1f5f9", border:"1px solid #e2e8f0",
                borderRadius:10, cursor:"pointer",
                fontFamily:"inherit", fontSize:14, fontWeight:600, color:"#374151",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8
              }}
            >
              <FiRefreshCw size={15}/> تحديث QR
            </button>
          )}

          {isConnected && (
            <button
              onClick={disconnect}
              disabled={loading}
              style={{
                flex:1, minWidth:140, padding:"12px 20px",
                background:"#fff", border:"2px solid #fca5a5",
                borderRadius:10, cursor: loading ? "not-allowed" : "pointer",
                fontFamily:"inherit", fontSize:14, fontWeight:600, color:"#dc2626",
                display:"flex", alignItems:"center", justifyContent:"center", gap:8
              }}
            >
              <FiLink2 size={15}/> فصل الرقم
            </button>
          )}

          <button
            onClick={manualCheck}
            title="الخطوة 1 — استعلام فقط عن حالة الرقم، لا يغيّر شيئًا"
            style={{
              padding:"12px 16px", background:"#f8fafc",
              border:"1px solid #e2e8f0", borderRadius:10, cursor:"pointer",
              fontFamily:"inherit", fontSize:14, color:"#64748b",
              display:"flex", alignItems:"center", gap:6
            }}
          >
            <FiRefreshCw size={14}/> تحديث الحالة
          </button>

          {/* v3.4.287 — إعادة تشغيل جلسة هذا الرقم (الخطوة 2) — داخل البطاقة بجانب التنظيف العميق */}
          <button
            onClick={restartSession}
            disabled={restarting || loading || !steps.status}
            title={!steps.status ? "🔒 متاح بعد الضغط على «تحديث الحالة» أو «ربط الرقم»" : "الخطوة 2 — يعيد تشغيل جلسة هذا الرقم مع بقاء الربط محفوظًا"}
            style={{
              padding:"12px 16px", background: steps.status ? "#eff6ff" : "#f8fafc",
              border: "1.5px solid " + (steps.status ? "#93c5fd" : "#e2e8f0"), borderRadius:10,
              cursor: (restarting || loading || !steps.status) ? "not-allowed" : "pointer",
              fontFamily:"inherit", fontSize:14, fontWeight:700, color: steps.status ? "#1d4ed8" : "#94a3b8",
              display:"flex", alignItems:"center", gap:6, opacity: (restarting || loading) ? 0.6 : 1
            }}
          >
            <FiRefreshCw size={14} className={restarting ? "wa-spin" : ""}/> {restarting ? "جاري إعادة التشغيل…" : "↻ إعادة تشغيل"}{!steps.status && " 🔒"}
          </button>

          {/* v3.4.046 — تنظيف عميق: يظهر للجلسات غير المتصلة فقط (علاج «No QR available») */}
          {isDisconnected && (
            <button
              onClick={() => { if (!steps.restart) return toast("جرّب «إعادة تشغيل» أولًا — التنظيف العميق هو الخطوة الأخيرة", { icon: "🔒" }); deepClean(); }}
              disabled={loading || !steps.restart}
              title={!steps.restart ? "🔒 متاح بعد محاولة «إعادة تشغيل»" : "الخطوة 3 — عندما يفشل جلب QR (No QR available): يقتل متصفح الجلسة العالق على السيرفر وينظف ملفاتها ويعيد بناءها (يتطلب QR جديد)"}
              style={{
                padding:"12px 16px", background: steps.restart ? "#fffbeb" : "#f8fafc",
                border:"1.5px solid " + (steps.restart ? "#fcd34d" : "#e2e8f0"), borderRadius:10, cursor: (loading || !steps.restart) ? "not-allowed" : "pointer",
                fontFamily:"inherit", fontSize:14, fontWeight:700, color: steps.restart ? "#b45309" : "#94a3b8",
                display:"flex", alignItems:"center", gap:6, opacity: loading ? 0.6 : 1
              }}
            >
              🧹 تنظيف عميق{!steps.restart && " 🔒"}
            </button>
          )}
        </div>

        {/* v3.4.287 — إرشادات الأزرار الثلاثة وتسلسلها */}
        <div style={{ marginTop: 14, border: "1px solid #e2e8f0", borderRadius: 10, background: "#f8fafc", padding: "10px 12px" }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginBottom: 6 }}>🧭 متى أستخدم كل زر؟ — التسلسل إلزامي: 1 ← 2 ← 3 (لا يُفعَّل زر إلا بعد محاولة الذي قبله)</div>
          <div style={{ display: "grid", gap: 6 }}>
            {WA_STEP_GUIDE.map(g => {
              const done = !!steps[g.k];
              const unlocked = g.k === "status" || (g.k === "restart" && steps.status) || (g.k === "clean" && steps.restart);
              return (
                <div key={g.k} style={{ display: "flex", gap: 8, alignItems: "flex-start", background: "#fff", border: "1px solid " + (done ? "#bbf7d0" : unlocked ? "#e2e8f0" : "#f1f5f9"), borderRadius: 8, padding: "7px 10px", opacity: unlocked || done ? 1 : 0.6 }}>
                  <span style={{ width: 22, height: 22, borderRadius: "50%", background: done ? "#16a34a" : unlocked ? "#1e293b" : "#cbd5e1", color: "#fff", fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{done ? "✓" : g.n}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 800, color: "#1e293b" }}>{g.icon} {g.title} <span style={{ fontSize: 10.5, fontWeight: 700, color: done ? "#166534" : unlocked ? "#1d4ed8" : "#94a3b8" }}>{done ? "— تمت المحاولة" : unlocked ? "— متاح الآن" : "— 🔒 بعد الخطوة " + (g.n - 1)}</span></div>
                    <div style={{ fontSize: 11.5, color: "#475569", lineHeight: 1.7 }}><b>متى:</b> {g.when}</div>
                    <div style={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.7 }}><b>ماذا يفعل:</b> {g.does}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>ملاحظة: «فصل الرقم» و«ربط الرقم» ليسا جزءًا من التسلسل — الفصل يلغي الربط نهائيًا ويحتاج QR جديدًا، فلا تستخدمه لحل مشاكل الإرسال.</div>
        </div>

        {/* v3.4.288 — سجل عملية الربط اللحظي */}
        <LinkLogPanel sessionId={session.id} open={logOpen} onToggle={() => setLogOpen(o => !o)} refreshKey={logTick} />
      </div>
    </div>
  );
}

export default function WhatsAppSettingsPage({ embedded = false }) {
  const [restarting, setRestarting] = React.useState(false);
  const [channelVisibility, setChannelVisibility] = React.useState({
    corporate: true, marketers: true, abubader2: true, abubader_biz: true
  });

  // Load visibility state
  React.useEffect(() => {
    appSettings.getWaChannels().then(r => {
      setChannelVisibility(prev => ({ ...prev, ...r.data }));
    }).catch(() => {});
  }, []);

  const handleToggleVisible = async (channelId, newValue) => {
    const prev = { ...channelVisibility };
    setChannelVisibility(v => ({ ...v, [channelId]: newValue }));
    try {
      await appSettings.updateWaChannels({ [channelId]: newValue });
      toast.success(newValue ? "تم إظهار القناة في القائمة" : "تم إخفاء القناة من القائمة");
    } catch (e) {
      setChannelVisibility(prev);
      toast.error("فشل تحديث الإعداد: " + (e.response?.data?.error || e.message));
    }
  };

  const restartWaServer = async () => {
    if (!window.confirm("هل تريد إعادة تشغيل سيرفر واتساب؟\nسيتم إعادة تشغيل جميع الجلسات.")) return;
    setRestarting(true);
    try {
      // إعادة تشغيل كلا الجلستين
      await Promise.allSettled([
        waApi.restart("corporate"),
        waApi.restart("marketers"),
        waApi.restart("abubader2"),
        waApi.restart("abubader_biz"),
      ]);
      alert("✅ تم إعادة تشغيل السيرفر — انتظر 10-15 ثانية ثم اضغط تحديث الحالة");
    } catch (e) {
      alert("⚠️ خطأ: " + (e.message || "فشل إعادة التشغيل"));
    }
    setRestarting(false);
  };

  const wrapper = embedded
    ? { padding: 0 }
    : { padding: 32, maxWidth: 900, margin: "0 auto" };
  return (
    <div style={wrapper}>
      <div style={{ marginBottom:28 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:8 }}>
          <FaWhatsapp size={32} color="#25D366"/>
          <h1 style={{ margin:0, fontSize:24, fontWeight:700, color:"#1e293b" }}>
            إعدادات واتساب
          </h1>
        </div>
        <p style={{ margin:0, color:"#64748b", fontSize:15 }}>
          إدارة ربط أرقام واتساب — اربط الرقمين لتتمكن من إرسال واستقبال الرسائل من المنصة
        </p>
      </div>

      {/* Instructions */}
      <div style={{
        background:"#fef9c3", border:"1px solid #fde047",
        borderRadius:12, padding:"14px 18px", marginBottom:28,
        fontSize:14, color:"#92400e", lineHeight:1.8
      }}>
        <div style={{ fontWeight:700, marginBottom:6 }}>📋 طريقة الربط:</div>
        ١. اضغط <strong>"ربط الرقم"</strong> للرقم المطلوب<br/>
        ٢. افتح واتساب على الهاتف ← الإعدادات ← الأجهزة المرتبطة ← ربط جهاز جديد<br/>
        ٣. امسح الـ QR Code بالكاميرا<br/>
        ٤. انتظر ثم ستظهر حالة "متصل" 🟢
      </div>

      <div style={{ display:"flex", justifyContent:"center", marginBottom:20 }}>
        <button onClick={restartWaServer} disabled={restarting}
          style={{
            padding:"14px 32px", background:"#fffbeb", border:"2px solid #fde68a",
            borderRadius:12, cursor: restarting ? "not-allowed" : "pointer",
            fontFamily:"inherit", fontSize:15, fontWeight:700, color:"#d97706",
            display:"flex", alignItems:"center", gap:10, opacity: restarting ? 0.6 : 1
          }}>
          <FiRefreshCw size={17} className={restarting ? "wa-spin" : ""}/> {restarting ? "جاري إعادة التشغيل..." : "🔄 إعادة تشغيل سيرفر واتساب"}
        </button>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24 }}>
        {SESSIONS.map(s => (
          <SessionCard key={s.id} session={s} visible={channelVisibility[s.id] !== false} onToggleVisible={handleToggleVisible}/>
        ))}
      </div>

      <div style={{ marginTop:24, padding:"14px 18px", background:"#f8fafc", borderRadius:12, fontSize:13, color:"#64748b" }}>
        <strong>ملاحظة:</strong> إذا واجهت مشاكل في الإرسال (timeout أو detached frame)، اضغط <strong style={{ color:"#d97706" }}>"إعادة تشغيل الجلسة"</strong> لحل المشكلة. إذا انقطع الاتصال تلقائياً، أعد الربط بمسح QR جديد.
      </div>
    </div>
  );
}
